import React, { useState } from 'react';
import { Check, Upload, ArrowRight, ArrowLeft } from 'lucide-react';
import { careerContent, formOptions, companyInfo } from '../data/mock';
import { toast } from 'sonner';

const Careers = () => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    linkedin: '',
    resume: null,
    resumeName: '',
    excelLevel: '',
    englishLevel: '',
    isInCollege: '',
    collegeCourse: '',
    collegePeriod: '',
    graduationYear: '',
    areaOfInterest: '',
    message: '',
    lgpdConsent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      if (file) {
        if (file.size > 5 * 1024 * 1024) {
          toast.error('Arquivo muito grande. Máximo: 5MB');
          return;
        }
        setFormData(prev => ({ 
          ...prev, 
          resume: file,
          resumeName: file.name
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.email && formData.phone && formData.city;
      case 2:
        return formData.resume !== null;
      case 3:
        if (formData.isInCollege === 'sim') {
          return formData.excelLevel && formData.englishLevel && formData.collegeCourse && formData.collegePeriod && formData.graduationYear;
        }
        return formData.excelLevel && formData.englishLevel && formData.isInCollege;
      case 4:
        return formData.areaOfInterest && formData.lgpdConsent;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(prev => prev + 1);
    } else {
      toast.error('Preencha todos os campos obrigatórios');
    }
  };

  const prevStep = () => setStep(prev => prev - 1);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(4)) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success('Currículo enviado com sucesso!');
  };

  if (isSubmitted) {
    return (
      <main className="pt-20 min-h-screen bg-[#F7FAFC]">
        <div className="container-main section-spacing">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-20 h-20 bg-[#3E6B2A] rounded-full flex items-center justify-center mx-auto mb-8">
              <Check size={40} className="text-white" />
            </div>
            <h1 className="heading-2 mb-4">Recebemos seu currículo!</h1>
            <p className="body-large mb-8">
              Obrigado por se candidatar. Nossa equipe analisará seu perfil 
              e entrará em contato caso haja uma oportunidade compatível.
            </p>
            <a
              href={`https://wa.me/${companyInfo.contact.whatsapp}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-secondary"
            >
              Falar no WhatsApp
            </a>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pt-20 min-h-screen bg-white">
      {/* Hero com imagem de trainees/jovens profissionais */}
      <section className="py-16 bg-[#F7FAFC]">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-xl">
              <span className="caption mb-4 block">Carreiras</span>
              <h1 className="heading-display mb-6">
                {careerContent.title}
              </h1>
              <p className="body-large">
                {careerContent.description}
              </p>
            </div>
            {/* Imagem de trainees - DIVERSIDADE: ambiente corporativo */}
            <div className="hidden lg:block">
              <div className="aspect-[4/3] rounded-lg overflow-hidden shadow-lg border border-[#E2E8F0]">
                <img 
                  src="https://images.pexels.com/photos/3184419/pexels-photo-3184419.jpeg?w=800&q=80" 
                  alt="Jovens profissionais em ambiente corporativo"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 bg-white border-b border-[#E2E8F0]">
        <div className="container-main">
          <div className="flex flex-wrap gap-4">
            {careerContent.benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 bg-[#F7FAFC] rounded-lg px-5 py-3">
                <Check size={16} className="text-[#3E6B2A]" />
                <span className="text-[#475569] text-sm font-medium">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="section-spacing bg-[#F7FAFC]">
        <div className="container-main">
          <div className="max-w-2xl mx-auto">
            {/* Step Indicator */}
            <div className="flex justify-center mb-12">
              <div className="flex items-center gap-2">
                {[1, 2, 3, 4].map((s) => (
                  <React.Fragment key={s}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm transition-colors ${
                      step === s 
                        ? 'bg-[#3E6B2A] text-white' 
                        : step > s 
                          ? 'bg-[#3E6B2A] text-white' 
                          : 'bg-[#E2E8F0] text-[#64748B]'
                    }`}>
                      {step > s ? <Check size={16} /> : s}
                    </div>
                    {s < 4 && (
                      <div className={`w-12 h-0.5 ${
                        step > s ? 'bg-[#3E6B2A]' : 'bg-[#E2E8F0]'
                      }`} />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 md:p-10 shadow-sm border border-[#E2E8F0]">
              {/* Step 1: Personal Data */}
              {step === 1 && (
                <div className="space-y-6">
                  <h2 className="heading-3 mb-6">Dados pessoais</h2>
                  
                  <div>
                    <label className="form-label">Nome completo *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Seu nome completo"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">E-mail *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="seu@email.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">Telefone/WhatsApp *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="(92) 99999-9999"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">Cidade/UF *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Ex: Manaus/AM"
                      required
                    />
                  </div>

                  <div>
                    <label className="form-label">LinkedIn (opcional)</label>
                    <input
                      type="url"
                      name="linkedin"
                      value={formData.linkedin}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="https://linkedin.com/in/seu-perfil"
                    />
                  </div>
                </div>
              )}

              {/* Step 2: Resume Upload */}
              {step === 2 && (
                <div className="space-y-6">
                  <h2 className="heading-3 mb-6">Currículo</h2>
                  
                  <div>
                    <label className="form-label">Anexar currículo (PDF ou DOC) *</label>
                    <div className="mt-2">
                      <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-[#E2E8F0] rounded-xl cursor-pointer hover:border-[#3E6B2A] transition-colors bg-[#F7FAFC]">
                        <div className="flex flex-col items-center justify-center py-6">
                          <Upload size={40} className="text-[#3E6B2A] mb-4" />
                          {formData.resumeName ? (
                            <p className="text-[#0F172A] font-medium">{formData.resumeName}</p>
                          ) : (
                            <>
                              <p className="text-[#475569] font-medium mb-1">Clique para enviar</p>
                              <p className="text-[#94A3B8] text-sm">PDF ou DOC (máx. 5MB)</p>
                            </>
                          )}
                        </div>
                        <input
                          type="file"
                          name="resume"
                          onChange={handleChange}
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Skills */}
              {step === 3 && (
                <div className="space-y-6">
                  <h2 className="heading-3 mb-6">Qualificações</h2>
                  
                  <div>
                    <label className="form-label">Domínio de Excel *</label>
                    <select
                      name="excelLevel"
                      value={formData.excelLevel}
                      onChange={handleChange}
                      className="form-input"
                      required
                    >
                      <option value="">Selecione</option>
                      {formOptions.excelLevels.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Nível de Inglês *</label>
                    <select
                      name="englishLevel"
                      value={formData.englishLevel}
                      onChange={handleChange}
                      className="form-input"
                      required
                    >
                      <option value="">Selecione</option>
                      {formOptions.englishLevels.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Você está na faculdade? *</label>
                    <select
                      name="isInCollege"
                      value={formData.isInCollege}
                      onChange={handleChange}
                      className="form-input"
                      required
                    >
                      <option value="">Selecione</option>
                      <option value="sim">Sim</option>
                      <option value="nao">Não</option>
                    </select>
                  </div>

                  {formData.isInCollege === 'sim' && (
                    <>
                      <div>
                        <label className="form-label">Qual curso? *</label>
                        <input
                          type="text"
                          name="collegeCourse"
                          value={formData.collegeCourse}
                          onChange={handleChange}
                          className="form-input"
                          placeholder="Ex: Ciências Contábeis"
                          required
                        />
                      </div>

                      <div>
                        <label className="form-label">Período atual *</label>
                        <select
                          name="collegePeriod"
                          value={formData.collegePeriod}
                          onChange={handleChange}
                          className="form-input"
                          required
                        >
                          <option value="">Selecione</option>
                          {formOptions.collegePeriods.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="form-label">Previsão de formatura *</label>
                        <select
                          name="graduationYear"
                          value={formData.graduationYear}
                          onChange={handleChange}
                          className="form-input"
                          required
                        >
                          <option value="">Selecione</option>
                          {formOptions.graduationYears.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Step 4: Area of Interest */}
              {step === 4 && (
                <div className="space-y-6">
                  <h2 className="heading-3 mb-6">Área de interesse</h2>
                  
                  <div>
                    <label className="form-label">Área de interesse *</label>
                    <select
                      name="areaOfInterest"
                      value={formData.areaOfInterest}
                      onChange={handleChange}
                      className="form-input"
                      required
                    >
                      <option value="">Selecione</option>
                      {formOptions.careerAreas.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Por que você quer a eLauria? (opcional)</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      className="form-input min-h-[120px] resize-none"
                      placeholder="Conte em 2-3 linhas por que você quer fazer parte da eLauria"
                    />
                  </div>

                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="lgpdConsent"
                      name="lgpdConsent"
                      checked={formData.lgpdConsent}
                      onChange={handleChange}
                      className="w-5 h-5 mt-0.5 rounded border-[#E2E8F0] text-[#3E6B2A] focus:ring-[#3E6B2A]"
                      required
                    />
                    <label htmlFor="lgpdConsent" className="text-[#64748B] text-sm">
                      Concordo com o tratamento dos meus dados pessoais conforme a 
                      <a href="/privacidade" className="text-[#3E6B2A] hover:underline"> Política de Privacidade</a> 
                      da eLauria (LGPD). *
                    </label>
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8 pt-6 border-t border-[#E2E8F0]">
                {step > 1 ? (
                  <button
                    type="button"
                    onClick={prevStep}
                    className="flex items-center gap-2 text-[#64748B] hover:text-[#0F172A] transition-colors font-medium"
                  >
                    <ArrowLeft size={18} />
                    Voltar
                  </button>
                ) : (
                  <div />
                )}

                {step < 4 ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    className="btn-primary"
                  >
                    Continuar
                    <ArrowRight size={18} />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary"
                  >
                    {isSubmitting ? 'Enviando...' : 'Enviar Currículo'}
                    {!isSubmitting && <ArrowRight size={18} />}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Careers;
